import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CornerDownLeft } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command';
import { useSearchableNavigation } from './use-navigation';

/**
 * Jump-to-page search.
 *
 * With ~70 pages behind nested menus, hunting through the tree was the slowest
 * part of using the old portal. ⌘K / Ctrl+K goes anywhere in two keystrokes,
 * and it only ever lists pages the user is actually allowed to open.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const items = useSearchableNavigation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  // Group by section so results keep the mental model of the sidebar. Grouping
  // on the translated heading, not the key, keeps sections that share a label
  // in one bucket in both languages.
  const grouped = items.reduce<Record<string, typeof items>>((acc, item) => {
    const key = t(item.sectionLabel ?? 'nav.sections.general');
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('shell.commandPlaceholder')} />
      <CommandList>
        <CommandEmpty>{t('shell.commandEmpty')}</CommandEmpty>
        {Object.entries(grouped).map(([section, sectionItems]) => (
          <CommandGroup key={section} heading={section}>
            {sectionItems.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.key}
                  // cmdk matches on `value`; folding in the parent and keywords
                  // means "timeslots" finds Booking → Events.
                  value={[
                    t(item.label),
                    item.parentLabel && t(item.parentLabel),
                    ...(item.keywords ?? []),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onSelect={() => {
                    navigate(item.to);
                    onOpenChange(false);
                  }}
                >
                  {Icon && <Icon className="text-muted-foreground" />}
                  <span>{t(item.label)}</span>
                  {item.parentLabel && (
                    <span className="text-muted-foreground text-xs">
                      {t('shell.commandIn', { parent: t(item.parentLabel) })}
                    </span>
                  )}
                  <CommandShortcut>
                    <CornerDownLeft className="size-3" />
                  </CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
